import { ExtendedModuleState, Module } from '@shared/Frontend/application-discovery/module'
import { Loader } from '@shared/Frontend/components/loader'
import { pageSearchParam } from '@shared/Frontend/components/paginator'
import { CookiebarState } from '@shared/Frontend/libraries/cookiebar/cookiebar.state'
import { route } from '@shared/Frontend/router'
import { setCookie } from '@shared/Frontend/utils/cookies'
import { Cookies, setConsentCookie, trackingCookies, videoCookies, yes } from '@shared/Frontend/utils/hoppingerCookies'
import { NotFoundPage } from '@shared/Frontend/utils/notFoundPage'
import { PageLocalizationLinks } from '@shared/Frontend/utils/routes.shared'
import { updateLanguage } from '@shared/Frontend/utils/updateLanguage'
import { fieldUpdater } from '@shared/Frontend/utils/updaters'
import { Viewport } from '@shared/Frontend/utils/viewport'
import { AsyncState, BasicUpdater, Maybe, unit } from 'ballerina-core'
import i18next from 'i18next'
import React from 'react'
import { CMSFooter } from './apps/web/cmsFooter/cmsFooter'
import { CMSMenu } from './apps/web/cmsMenu/cmsMenu'
import { ContactBlock } from './apps/web/cmsPage/blocks/contactBlock/contactBlock'
import { ContactGridBlock } from './apps/web/cmsPage/blocks/contactGridBlock/contactGridBlock'
import { CTABlock } from './apps/web/cmsPage/blocks/ctaBlock/ctaBlock'
import { DownloadBlock } from './apps/web/cmsPage/blocks/downloadBlock/downloadBlock'
import { ExpandableBlock } from './apps/web/cmsPage/blocks/expandableBlock/expandableBlock'
import { FactsAndFiguresBlock } from './apps/web/cmsPage/blocks/factsAndFiguresBlock/factsAndFiguresBlock'
import { FeaturedContentBlock } from './apps/web/cmsPage/blocks/featuredContentBlock/featuredContentBlock'
import { GridBlock } from './apps/web/cmsPage/blocks/gridBlock/gridBlock'
import { IframeBlock } from './apps/web/cmsPage/blocks/iframe/iframe'
import { LinkListBlock } from './apps/web/cmsPage/blocks/linkListBlock/linkListBlock'
import { LogoWallBlock } from './apps/web/cmsPage/blocks/logoWallBlock/logoWallBlock'
import { MediaBlock } from './apps/web/cmsPage/blocks/mediaBlock/mediaBlock'
import { MediaGalleryBlock } from './apps/web/cmsPage/blocks/mediaGalleryBlock/mediaGalleryBlock'
import { QuoteBlock } from './apps/web/cmsPage/blocks/quoteBlock/quoteBlock'
import { RecentContentBlock } from './apps/web/cmsPage/blocks/recentContentBlock/recentContentBlock'
import { RichDownloadsBlock } from './apps/web/cmsPage/blocks/richDownloadsBlock/richDownloadsBlock'
import { SidebarNavigationBlock } from './apps/web/cmsPage/blocks/sidebarNavigation/sidebarNavigation'
import { TableBlock } from './apps/web/cmsPage/blocks/tableBlock/tableBlock'
import { TextBlock, TextMiniBlock } from './apps/web/cmsPage/blocks/textBlock/textBlock'
import { TextReadMoreBlock } from './apps/web/cmsPage/blocks/textReadMoreBlock/textReadMoreBlock'
import { TextWithImageBlock } from './apps/web/cmsPage/blocks/textWithImageBlock/textWithImageBlock'
import { CMSPage } from './apps/web/cmsPage/cmsPage'
import { Filters } from './libraries/cmsPage/filters/filters.layout'
import { Teasers } from './apps/web/cmsPage/teasers/teasers'
import { SearchBar } from './apps/web/searchBar/searchBar'
import { SiteBanner } from './apps/web/siteBanner/siteBanner'
import { CMSFooterState, initialCMSFooterState } from './libraries/cmsFooter/cmsFooter.state'
import { CMSFooterTemplate } from './libraries/cmsFooter/cmsFooter.template'
import { CMSMenuState, initialCMSMenuState } from './libraries/cmsMenu/cmsMenu.state'
import { CMSMenuTemplate } from './libraries/cmsMenu/cmsMenu.template'
import { CMSPageState, initialCMSPageState } from './libraries/cmsPage/cmsPage.state'
import { CMSPageTemplate, CMSPageView } from './libraries/cmsPage/cmsPage.template'
import { initialSearchBarState, SearchBarState } from './libraries/searchBar/searchBar.state'
import { SearchBarTemplate } from './libraries/searchBar/searchBar.template'
import { initialSiteBannerState, SiteBannerState } from './libraries/siteBanner/siteBanner.state'
import { SiteBannerTemplate } from './libraries/siteBanner/siteBanner.template'
import { BmiCalculatorBlock } from './apps/web/cmsPage/blocks/bmiCalculator/bmiCalculator'
import { None } from '@shared/Frontend/components/none'
import { SimpleGridBlock } from './apps/web/cmsPage/blocks/gridBlock/simpleGridBlock'

export type StateRequirements = { cookiebar: CookiebarState; printMode: boolean; viewport: Viewport }
export type CMSModuleState = {
  searchBar: SearchBarState
  cmsMenu: CMSMenuState
  cmsFooter: CMSFooterState
  siteBanner: SiteBannerState
}

type PagesRequirements = {}
export type CMSModulePages = {
  cmsPage: {
    url: [
      { language: string },
      { _: Maybe<string> }, // the wildcard (* in the url pattern) match is collected in the _ by the url-pattern library
      { [pageSearchParam]: Maybe<string> },
      { [querySearchParam]: Maybe<string> }
    ]
    cmsPage: CMSPageState
  }
  preview: {
    url: [
      { bundle: string },
      { nodeId: number },
      { nodeRevision: number },
      { [pageSearchParam]: Maybe<string> },
      { [querySearchParam]: Maybe<string> },
      { token: string }
    ]
    cmsPage: CMSPageState
  }
}

export const querySearchParam = 'searchQuery'

type ExtendedCMSModuleState<allApplicationState> = ExtendedModuleState<
  allApplicationState & CMSModuleState & StateRequirements,
  CMSModulePages
>

export const cmsModule: Module<CMSModulePages, CMSModuleState, StateRequirements, PagesRequirements> = {
  new: <allApplicationState extends CMSModuleState & StateRequirements>() => {
    type State = ExtendedCMSModuleState<allApplicationState>
    const MappedCMSPageTemplate = CMSPageTemplate.mapState<State>((updater) => (state) => {
      const newCMSPage = updater(state.page.cmsPage)
      let localizationLinks: PageLocalizationLinks = {}
      if (AsyncState.Operations.hasValue(newCMSPage) && newCMSPage.value?.localizationLinks !== undefined) {
        Object.entries(newCMSPage.value.localizationLinks).forEach(
          ([language, data]) => (localizationLinks[language] = data.slug)
        )
      }
      return { ...state, page: { ...state.page, cmsPage: newCMSPage, localizationLinks } }
    })
    return {
      routeBuilders: () => ({
        cmsPage: { make: (params) => ({ kind: 'cmsPage', params, cmsPage: initialCMSPageState() }) },
        preview: { make: (params) => ({ kind: 'preview', params, cmsPage: initialCMSPageState() }) },
      }),
      routeUpdaters: function (this) {
        return {
          cmsPage: {
            jumpTo: (params) => {
              const newPage = this.routeBuilders().cmsPage.make(params)
              const sameLanguage = i18next.language === params.language
              if (!sameLanguage) updateLanguage(params.language)
              return (state) => ({
                ...state,
                page:
                  state.page.kind === 'cmsPage' &&
                  state.page.params.language === params.language &&
                  state.page.params._ === params._
                    ? { ...newPage, cmsPage: initialCMSPageState(state.page.cmsPage) }
                    : newPage,
                searchBar: { ...state.searchBar, query: params[querySearchParam] },
                cmsFooter: sameLanguage ? state.cmsFooter : initialCMSFooterState,
                cmsMenu: sameLanguage ? state.cmsMenu : initialCMSMenuState,
              })
            },
            url: '/:language*',
          },
          preview: {
            jumpTo: (params) => {
              const newPage = this.routeBuilders().preview.make(params)
              return (state) => ({
                ...state,
                page:
                  state.page.kind === 'preview' && state.page.params.nodeId === params.nodeId
                    ? { ...newPage, cmsPage: initialCMSPageState(state.page.cmsPage) }
                    : newPage,
                searchBar: { ...state.searchBar, query: params[querySearchParam] },
              })
            },
            url: '/preview/:bundle([a-zA-Z]+)/:nodeId([0-9]+)(/:nodeRevision([0-9]+))',
          },
        }
      },
      routeObject: function (this) {
        return {
          preview: route(this.routeUpdaters().preview.url, this.routeUpdaters().preview.jumpTo),
          cmsPage: route(this.routeUpdaters().cmsPage.url, this.routeUpdaters().cmsPage.jumpTo),
        }
      },
      routes: function (this) {
        return [this.routeObject().preview, this.routeObject().cmsPage]
      },
      tryPageSwitch: ({ page, cookiebar, viewport, printMode }, setState: (_: BasicUpdater<State>) => void) => {
        switch (page.kind) {
          case 'preview':
          case 'cmsPage':
            return (
              <MappedCMSPageTemplate
                context={{
                  ...page.cmsPage,
                  viewport,
                  printMode,
                  page: Maybe.Operations.map(parseInt)(page.params.page) || 1,
                  videoCookieAccepted: cookiebar.cookies[videoCookies] === yes,
                  trackingCookieAccepted: cookiebar.cookies[trackingCookies] === yes,
                  ...(page.kind == 'preview'
                    ? {
                        preview: true,
                        bundle: page.params.bundle,
                        nodeId: page.params.nodeId,
                        nodeRevision: page.params.nodeRevision,
                        token: page.params.token,
                        filters: AsyncState.Default.unloaded(),
                      }
                    : { preview: false, slug: page.params._, language: page.params.language, filters: AsyncState.Default.unloaded() }),
                }}
                setState={setState}
                foreignMutations={{
                  acceptVideoCookie: (): void => {
                    setState(
                      fieldUpdater<State>()('cookiebar')((state): CookiebarState => {
                        const selectedCookies: Cookies = { ...state.selectedCookies, [videoCookies]: yes }
                        return { ...state, selectedCookies: selectedCookies, cookies: selectedCookies }
                      })
                    )
                    setCookie(videoCookies, yes)
                  },
                  acceptTrackingCookie: (): void => {
                    setState(
                      fieldUpdater<State>()('cookiebar')((state): CookiebarState => {
                        const selectedCookies: Cookies = { ...state.selectedCookies, [trackingCookies]: yes }
                        return { ...state, selectedCookies: selectedCookies, cookies: selectedCookies }
                      })
                    )
                    setConsentCookie(trackingCookies, yes)
                  },
                }}
                view={cmsPageView}
              />
            )
        }
      },
      header: (state, setState) => (
        <>
          <CMSMenuTemplate
            context={state.cmsMenu}
            setState={(updater) => setState((state) => ({ ...state, cmsMenu: updater(state.cmsMenu) }))}
            foreignMutations={unit}
            view={{ CMSMenu }}
          />
          {/* <SearchBarTemplate
            context={state.searchBar}
            setState={(updater) => setState((state) => ({ ...state, searchBar: updater(state.searchBar) }))}
            foreignMutations={unit}
            view={{ SearchBar }}
          /> */}
        </>
      ),
      footer: (state, setState) => (
        <CMSFooterTemplate
          context={state.cmsFooter}
          setState={(updater) => setState((state) => ({ ...state, cmsFooter: updater(state.cmsFooter) }))}
          foreignMutations={unit}
          view={{ CMSFooter }}
        />
      ),
      root: (state, setState) => (
        <SiteBannerTemplate
          setState={(updater) => setState((state) => ({ ...state, siteBanner: updater(state.siteBanner) }))}
          context={state.siteBanner}
          foreignMutations={unit}
          view={{ SiteBanner }}
        />
      ),
    }
  },
  initialState: (): CMSModuleState => ({
    searchBar: initialSearchBarState(''),
    siteBanner: initialSiteBannerState,
    cmsMenu: initialCMSMenuState,
    cmsFooter: initialCMSFooterState,
  }),
  fullScreenPages: [],
}

const cmsPageView: CMSPageView = {
  Loader,
  None,
  NotFoundPage,
  Teasers,
  Filters,
  CMSPage,
  // Blocks
  BmiCalculatorBlock,
  ContactBlock,
  ContactGridBlock,
  CTABlock,
  DownloadBlock,
  ExpandableBlock,
  FactsAndFiguresBlock,
  FeaturedContentBlock,
  GridBlock,
  SimpleGridBlock,
  IframeBlock,
  MediaBlock,
  MediaGalleryBlock,
  TextWithImageBlock,
  LinkListBlock,
  LogoWallBlock,
  QuoteBlock,
  RecentContentBlock,
  RichDownloadsBlock,
  SidebarNavigationBlock,
  TableBlock,
  TextReadMoreBlock,
  TextBlock,
  TextMiniBlock,
}
